import Button from '@components/Button/Button';
import Dialog from '@components/Dialog/Dialog';
import { BRAND_COLORS } from '@components/EmailSignaturev2/consts';
import { EmailSignatureSocialLinkTypes } from '@components/EmailSignaturev2/types';
import {
  faFacebook,
  faInstagram,
  faLinkedinIn,
  faXTwitter,
  faYoutube,
} from '@fortawesome/free-brands-svg-icons';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { faText } from '@fortawesome/pro-regular-svg-icons';
import { faEnvelope, faMapMarkerAlt, faPaintBrush, faPhone } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AnimatePresence, motion } from 'motion/react';
import { type FC, useCallback, useState } from 'react';

import styles from './OnboardingHelpDialog.module.scss';

interface OnboardingIntroductionDialogProps {
  show: boolean;
  handleChangeShow: (newShowState: boolean) => void;
}

const HELP_MODE = {
  CONTACT: 'CONTACT',
  IMAGES: 'IMAGES',
  SOCIAL: 'SOCIAL',
  DESIGN: 'DESIGN',
  BANNER: 'BANNER',
} as const;

type HelpMode = keyof typeof HELP_MODE;

const OnboardingHelpDialog: FC<React.PropsWithChildren<OnboardingIntroductionDialogProps>> = ({
  show,
  handleChangeShow,
}) => {
  const [mode, setMode] = useState<HelpMode>(HELP_MODE.CONTACT);

  const handleCloseDialog = useCallback(() => {
    setMode(HELP_MODE.CONTACT);
    handleChangeShow(false);
  }, [handleChangeShow]);

  const handleClickNext = useCallback(() => {
    switch (mode) {
      case HELP_MODE.CONTACT:
        return HELP_MODE.IMAGES;
      case HELP_MODE.IMAGES:
        return HELP_MODE.SOCIAL;
      case HELP_MODE.SOCIAL:
        return HELP_MODE.DESIGN;
      case HELP_MODE.DESIGN:
        return HELP_MODE.BANNER;
      default:
        return HELP_MODE.CONTACT;
    }
  }, [mode]);

  return (
    <Dialog show={show} ariaLabel={'Features Help'} onClose={handleCloseDialog} showCloseButton={false}>
      <OnboardingIntroductionDialogContainer>
        <IllustrationBox>
          <AnimatePresence>
            {mode === HELP_MODE.CONTACT && <ContactDetailsIllustration />}
            {mode === HELP_MODE.IMAGES && <ImagesIllustration />}
            {mode === HELP_MODE.SOCIAL && <SocialLinksIllustration />}
            {mode === HELP_MODE.DESIGN && <DesignIllustration />}
            {mode === HELP_MODE.BANNER && <BannerIllustration />}
          </AnimatePresence>
        </IllustrationBox>

        <AnimatePresence>
          {mode === HELP_MODE.CONTACT && <FeatureTitle>Add your details</FeatureTitle>}
          {mode === HELP_MODE.IMAGES && <FeatureTitle>Personalise with images</FeatureTitle>}
          {mode === HELP_MODE.SOCIAL && <FeatureTitle>Link to favourite accounts</FeatureTitle>}
          {mode === HELP_MODE.DESIGN && <FeatureTitle>Customise your design</FeatureTitle>}
          {mode === HELP_MODE.BANNER && <FeatureTitle>Add a banner</FeatureTitle>}
        </AnimatePresence>

        <AnimatePresence>
          {mode === HELP_MODE.CONTACT && (
            <FeatureDescription>
              Add your personal information and contact information to your email signature.
            </FeatureDescription>
          )}
          {mode === HELP_MODE.IMAGES && (
            <FeatureDescription>
              Personalize your email signature by adding an image of yourself, a company logo or another
              image.
            </FeatureDescription>
          )}
          {mode === HELP_MODE.SOCIAL && (
            <FeatureDescription>
              Add links to your favorite social media accounts and websites so that recipients can access all
              your channels.
            </FeatureDescription>
          )}
          {mode === HELP_MODE.DESIGN && (
            <FeatureDescription>
              Make your signature pop by customizing the design to suit yourself, your company or your
              business.
            </FeatureDescription>
          )}
          {mode === HELP_MODE.BANNER && (
            <FeatureDescription>
              Add a custom banner, environmental footer or legal disclaimer to complete your signature,
              it&apos;s up to you!
            </FeatureDescription>
          )}
        </AnimatePresence>

        <DotButtons activeTab={mode} handleChangeTab={setMode} />

        <FeatureButtons
          isFinalStep={mode === HELP_MODE.BANNER}
          handleClose={handleCloseDialog}
          handleNext={() => {
            setMode(handleClickNext);
          }}
        />
      </OnboardingIntroductionDialogContainer>
    </Dialog>
  );
};

const OnboardingIntroductionDialogContainer: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  return <div className={styles['feature-introduction-container']}>{children}</div>;
};

const IllustrationBox: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  return <div className={styles['illustration-box']}>{children}</div>;
};

const FeatureTitle: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={styles['feature-title']}
    >
      {children}
    </motion.div>
  );
};

const FeatureDescription: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={styles['feature-description']}
    >
      {children}
    </motion.div>
  );
};

interface DotButtonsProps {
  activeTab: HelpMode;
  handleChangeTab: (mode: HelpMode) => void;
}

interface DotButtonProps extends DotButtonsProps {
  buttonMode: HelpMode;
}

const DotButton: FC<React.PropsWithChildren<DotButtonProps>> = ({
  buttonMode,
  activeTab,
  handleChangeTab,
}) => {
  return (
    <button
      onClick={() => handleChangeTab(buttonMode)}
      className={styles['dot-button']}
      data-is-active={buttonMode === activeTab}
    />
  );
};

const DotButtons: FC<React.PropsWithChildren<DotButtonsProps>> = ({ handleChangeTab, activeTab }) => {
  return (
    <div className={styles['dot-buttons-container']}>
      <DotButton buttonMode={HELP_MODE.CONTACT} activeTab={activeTab} handleChangeTab={handleChangeTab} />
      <DotButton buttonMode={HELP_MODE.IMAGES} activeTab={activeTab} handleChangeTab={handleChangeTab} />
      <DotButton buttonMode={HELP_MODE.SOCIAL} activeTab={activeTab} handleChangeTab={handleChangeTab} />
      <DotButton buttonMode={HELP_MODE.DESIGN} activeTab={activeTab} handleChangeTab={handleChangeTab} />
      <DotButton buttonMode={HELP_MODE.BANNER} activeTab={activeTab} handleChangeTab={handleChangeTab} />
    </div>
  );
};

interface FeatureButtonsProps {
  handleClose: () => void;
  handleNext: () => void;
  isFinalStep: boolean;
}

const FeatureButtons: FC<React.PropsWithChildren<FeatureButtonsProps>> = ({
  handleClose,
  handleNext,
  isFinalStep,
}) => {
  return (
    <div className={styles['feature-buttons-container']}>
      <Button onClick={handleClose} variant="subtle" type="default">
        Exit
      </Button>
      <Button onClick={isFinalStep ? handleClose : handleNext}>{isFinalStep ? 'Done' : 'Next'}</Button>
    </div>
  );
};

const IllustrationContainer: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={styles['illustration-container']}
    >
      {children}
    </motion.div>
  );
};

const ContactDetailsIllustration: FC<React.PropsWithChildren<unknown>> = () => {
  return (
    <IllustrationContainer>
      <div className={styles['option-pill']}>
        <FontAwesomeIcon icon={faEnvelope} />
        <div className={styles['long-details']} />
      </div>
      <div className={styles['option-pill']}>
        <FontAwesomeIcon icon={faPhone} />
        <div className={styles['medium-details']} />
      </div>
      <div className={styles['option-pill']}>
        <FontAwesomeIcon icon={faMapMarkerAlt} />
        <div className={styles['short-details']} />
      </div>
    </IllustrationContainer>
  );
};

const ImagesIllustration: FC<React.PropsWithChildren<unknown>> = () => {
  return (
    <IllustrationContainer>
      <img className={styles['image-profile']} alt="Personal profile" src="/img/profile-image-eclipse.png" />
      <div className={styles['goldline-logo-container']}>
        <img alt="Goldline" src="/img/goldline.svg" />
        <span className={styles['logo-label']}>goldline</span>
      </div>
    </IllustrationContainer>
  );
};

const SocialLinksIllustration: FC<React.PropsWithChildren<unknown>> = () => {
  return (
    <IllustrationContainer>
      <div className={styles['social-icons-container']}>
        <div
          className={styles['icon-container']}
          style={{ color: BRAND_COLORS[EmailSignatureSocialLinkTypes.LINKEDIN] }}
        >
          <FontAwesomeIcon icon={faLinkedinIn} />
        </div>
        <div
          style={{ color: BRAND_COLORS[EmailSignatureSocialLinkTypes.FACEBOOK] }}
          className={styles['icon-container']}
        >
          <FontAwesomeIcon icon={faFacebook} />
        </div>
        <div
          style={{ color: BRAND_COLORS[EmailSignatureSocialLinkTypes.TWITTER] }}
          className={styles['icon-container']}
        >
          <FontAwesomeIcon icon={faXTwitter} />
        </div>
        <div
          style={{ color: BRAND_COLORS[EmailSignatureSocialLinkTypes.YOUTUBE] }}
          className={styles['icon-container']}
        >
          <FontAwesomeIcon icon={faYoutube} />
        </div>
        <div
          style={{ color: BRAND_COLORS[EmailSignatureSocialLinkTypes.INSTAGRAM] }}
          className={styles['icon-container']}
        >
          <FontAwesomeIcon icon={faInstagram} />
        </div>

        <div className={styles['add-more-container']}>
          <FontAwesomeIcon icon={faPlus} />
          <span>more</span>
        </div>
      </div>
    </IllustrationContainer>
  );
};

const DesignIllustration: FC<React.PropsWithChildren<unknown>> = () => {
  return (
    <IllustrationContainer>
      <div className={styles['styles-examples-box']}>
        <div className={styles['styles-examples-container']}>
          <div
            style={{ color: BRAND_COLORS[EmailSignatureSocialLinkTypes.INSTAGRAM] }}
            className={styles['icon-container']}
          >
            <div className={styles['green-circle']} />
          </div>
          <div className={styles['icon-container']}>
            <FontAwesomeIcon icon={faPaintBrush} />
          </div>
          <div className={styles['save-contact-rounded']}>Save Contact</div>
        </div>
        <div className={styles['styles-examples-container']}>
          <div
            style={{ color: BRAND_COLORS[EmailSignatureSocialLinkTypes.INSTAGRAM] }}
            className={styles['icon-container']}
          >
            <div className={styles['blue-circle']} />
          </div>
          <div className={styles['icon-container']}>
            <FontAwesomeIcon icon={faText} />
          </div>
          <div className={styles['save-contact-slightly-rounded']}>Save Contact</div>
        </div>
      </div>
    </IllustrationContainer>
  );
};

const BannerIllustration: FC<React.PropsWithChildren<unknown>> = () => {
  return (
    <IllustrationContainer>
      <div className={styles['banner-silhouette']}>
        <div className={styles['top-section']}>
          <div className={styles['silhouette-circle']}></div>
          <div className={styles['silhouette-details']}>
            <div className={styles['long-details']}></div>
            <div className={styles['medium-details']}></div>
            <div className={styles['short-details']}></div>
          </div>
        </div>

        <div className={styles['second-section']}>
          <div className={styles['social-icon']}></div>
          <div className={styles['social-icon']}></div>
          <div className={styles['social-icon']}></div>
          <div className={styles['social-icon']}></div>
        </div>

        <div className={styles['third-section']}></div>
      </div>
    </IllustrationContainer>
  );
};

export { OnboardingHelpDialog };
